import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { Button } from '../components/Common/Button';
import { IconsLAccent01 } from '../components/Icons/Icons_L';
import { LoginBack, LoginHeader } from '../components/Common/Card';
import { Check, Clock, Cross } from '../components/Icons/CustomIcons';

/**
 * Statusseite für Löschanfragen, die Facebook an uns schickt (Data Deletion
 * Callback). Meta gibt dem Nutzer die URL dieser Seite zusammen mit dem
 * Bestätigungscode aus.
 * URL-Muster: /data-deletion-status?code=<confirmation_code>
 */

// Backend-Status -> Anzeige
const STATUS_TEXTE = {
  completed: {
    titel: 'Daten gelöscht',
    text: 'Ihr Konto und die damit verknüpften persönlichen Daten wurden vollständig gelöscht.',
    icon: Check,
  },
  pending: {
    titel: 'In Bearbeitung',
    text: 'Ihre Löschanfrage ist bei uns eingegangen und wird bearbeitet. Bitte prüfen Sie diese Seite später noch einmal.',
    icon: Clock,
  },
  not_found: {
    titel: 'Keine Daten vorhanden',
    text: 'Zu Ihrem Facebook-Konto waren bei uns keine Daten (mehr) gespeichert. Es gibt nichts zu löschen.',
    icon: Check,
  },
  failed: {
    titel: 'Löschung fehlgeschlagen',
    text: 'Die Löschung konnte nicht abgeschlossen werden. Bitte melden Sie sich bei uns, damit wir das von Hand erledigen.',
    icon: Cross,
  },
};

const DataDeletionStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code') || '';

  const [status, setStatus] = useState('loading'); // 'loading' | Backend-Status | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setMessage('Kein Bestätigungscode in der Adresse gefunden.');
      return;
    }

    const laden = async () => {
      try {
        const response = await authAPI.dataDeletionStatus(code);
        setStatus(response.data.status);
      } catch (err) {
        setStatus('error');
        setMessage(
          err.response?.data?.error ||
            'Der Status konnte nicht abgefragt werden. Bitte versuchen Sie es später erneut.'
        );
      }
    };

    laden();
  }, [code]);

  const useIsDesktop = () => {
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

    useEffect(() => {
      const handler = () => setIsDesktop(window.innerWidth >= 1024);
      window.addEventListener('resize', handler);
      return () => window.removeEventListener('resize', handler);
    }, []);

    return isDesktop;
  };

  const isDesktop = useIsDesktop();

  const bgStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/${
      isDesktop ? 'GrosselternMitKindern.png' : 'GrosselternMitKindernLeft.png'
    })`,
  };

  const IconMain = ({ Icon }) => (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconsLAccent01 size={84} />
        <Icon size={28} color="#fff" style={{ position: 'absolute' }} />
      </div>
    </div>
  );

  const anzeige = STATUS_TEXTE[status];

  return (
    <div className="auth-fullpage" style={bgStyle}>
      <LoginBack returnTo="/" />
      <div>
        <LoginHeader
          title="Status Ihrer Löschanfrage"
          subtitle="Hier sehen Sie, was mit den Daten zu Ihrem Facebook-Login passiert ist."
        />
      </div>
      <div className="auth-fullpage-panel">
        <div className="auth-panel-body">
          {status === 'loading' && (
            <div className="ml-success">
              <div className="ml-success-icon">⏳</div>
              <h2 className="ml-success-title">Status wird abgefragt…</h2>
            </div>
          )}

          {anzeige && (
            <div className="ml-success">
              <IconMain Icon={anzeige.icon} />
              <h2 className="ml-success-title">{anzeige.titel}</h2>
              <p>{anzeige.text}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="ml-success">
              <IconMain Icon={Cross} />
              <h2 className="ml-success-title">Status nicht abrufbar</h2>
              <p className="ml-success-hint">{message}</p>
            </div>
          )}

          {code && (
            <p className="ml-success-hint" style={{ textAlign: 'center', wordBreak: 'break-all' }}>
              Bestätigungscode: <strong>{code}</strong>
            </p>
          )}

          <Button className="w-full" onClick={() => navigate('/')}>
            Zur Startseite
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionStatus;
